import { motion } from "framer-motion";
import SectionBadge from "../ui/SectionBadge";
import {
  CheckmarkCircle02Icon,
  ArrowLeft02Icon,
  SparklesIcon,
  ShieldEnergyIcon,
} from "hugeicons-react";
import Magnetic from "../ui/Magnetic";
import { useMobilePerformance } from "../../hooks/useMobilePerformance";

export default function BookingCTA({
  onOpenBooking,
  packages = [],
}: {
  onOpenBooking?: () => void;
  packages?: any[];
}) {
  const { shouldReduceAnimations } = useMobilePerformance();
  // Get the most popular package (first one with badge, or first one)
  const featuredPackage =
    packages.find((pkg) => pkg.badgeLabel) || packages[0] || null;

  return (
    <section
      id="booking"
      className="py-20 md:py-24 relative overflow-hidden bg-[#050000]"
      dir="rtl"
    >
      {/* Seamless Transition Blends - Enhanced Masking */}
      <div className="absolute top-0 left-0 right-0 h-40 bg-gradient-to-b from-black via-black/80 to-transparent z-20 pointer-events-none" />
      <div className="absolute bottom-0 left-0 right-0 h-40 bg-gradient-to-t from-black via-black/80 to-transparent z-20 pointer-events-none" />

      {/* Background Atmosphere - "Alive" Version */}
      <div className="absolute inset-0 pointer-events-none">
          {/* Vibrant Ambient Glows */}
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,_rgba(220,10,50,0.15)_0%,_transparent_70%)] opacity-60" />
          
          {/* Animated Heat Orbs - More intense & lively */}
          {!shouldReduceAnimations && (
            <>
              <motion.div 
                animate={{ 
                  opacity: [0.2, 0.4, 0.2],
                  scale: [1, 1.2, 1],
                  x: [0, 20, 0],
                  y: [0, -20, 0]
                }}
                transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
                className="absolute top-[10%] right-[15%] w-[500px] h-[500px] bg-primary/25 blur-[120px] rounded-full mix-blend-screen" 
              />
              <motion.div 
                animate={{ 
                  opacity: [0.15, 0.35, 0.15],
                  scale: [1.2, 1, 1.2],
                  x: [0, -30, 0],
                  y: [0, 30, 0]
                }}
                transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
                className="absolute bottom-[10%] left-[10%] w-[600px] h-[600px] bg-red-600/15 blur-[140px] rounded-full mix-blend-screen" 
              />
            </>
          )}

          {/* Tactical Grid - Subtler but present */}
          <div className="absolute inset-0 opacity-[0.02] [background-image:linear-gradient(to_right,#fff_1px,transparent_1px),linear-gradient(to_bottom,#fff_1px,transparent_1px)] [background-size:50px_50px]" />
      </div>

      <div className="container mx-auto px-4 relative z-30">
        <div className="max-w-6xl mx-auto">
          {/* Main Promotional Card - Compacted & Vibrant */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="relative rounded-[40px] overflow-hidden border border-white/10 bg-[#0c0c0c]/60 backdrop-blur-2xl p-8 md:p-12 lg:p-14 shadow-[0_0_50px_rgba(0,0,0,0.5)]"
          >
            {/* Top Light Stroke */}
            <div className="absolute top-0 left-10 right-10 h-[1.5px] bg-gradient-to-r from-transparent via-primary/40 to-transparent" />
            
            <div className="relative z-10">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
                
                {/* Content Side (Right) */}
                <div className="lg:col-span-12 xl:col-span-7 text-right">
                  {/* Tag */}
                  <SectionBadge text="فرصة حصرية" icon={SparklesIcon} />

                  {/* Headline */}
                  <h2 className="text-4xl md:text-6xl font-black text-white mb-6 leading-tight tracking-tight">
                    سيارتك تستحق{" "}
                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-orange-500 drop-shadow-[0_0_20px_rgba(255,30,30,0.4)]">الأفضل</span>
                  </h2>

                  {/* Summary */}
                  <p className="text-gray-300 text-base md:text-lg mb-10 max-w-xl leading-relaxed font-bold">
                    انضم لنخبة عشاق السيارات. احصل على خصم{" "}
                    <span className="text-primary font-black border-b-2 border-primary/30 pb-0.5">20%</span> حصري عند
                    الحجز الرقمي لأول مرة.
                  </p>

                  {/* Mini Feature Set */}
                  <div className="flex flex-wrap gap-6 mb-10">
                    {[
                      { label: "ضمان شامل", icon: <ShieldEnergyIcon size={18} /> },
                      { label: "دفع آمن", icon: <CheckmarkCircle02Icon size={18} /> },
                      { label: "خدمة VIP", icon: <SparklesIcon size={18} /> },
                    ].map((feat, i) => (
                      <div key={i} className="flex items-center gap-2.5 text-white/80 font-bold text-sm">
                        <div className="w-8 h-8 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center text-primary group-hover:scale-110 transition-transform">
                          {feat.icon}
                        </div>
                        <span>{feat.label}</span>
                      </div>
                    ))}
                  </div>

                  {/* CTA Button */}
                  <div className="flex justify-start">
                    <Magnetic>
                      <button
                        onClick={() => onOpenBooking?.()}
                        className="group relative h-16 px-10 text-lg font-black rounded-xl overflow-hidden shadow-[0_15px_30px_rgba(220,10,50,0.3)] transition-transform hover:scale-105 active:scale-95"
                      >
                         <div className="absolute inset-0 bg-primary" />
                         <div className="relative z-10 flex items-center gap-3 text-white">
                            <span>ابدأ رحلة التغيير</span>
                            <ArrowLeft02Icon size={20} className="group-hover:-translate-x-1.5 transition-transform" />
                         </div>
                         <div className="absolute top-0 left-[-100%] group-hover:left-[100%] w-full h-full bg-gradient-to-r from-transparent via-white/30 to-transparent transition-all duration-700 ease-in-out" />
                      </button>
                    </Magnetic>
                  </div>
                </div>

                {/* Card Side (Left) - Compacter & More "Alive" */}
                {featuredPackage && (
                  <div className="lg:col-span-12 xl:col-span-5 relative hidden xl:block h-full">
                    <motion.div
                      whileHover={{ scale: 1.02 }}
                      className="relative h-[380px] w-full rounded-[30px] overflow-hidden border border-white/10 bg-[#0c0c0c] shadow-2xl group"
                    >
                      {/* Vibrant Image Base */}
                      <div className="absolute inset-0 opacity-40 group-hover:opacity-50 transition-opacity">
                         <div className="absolute inset-0 bg-gradient-to-t from-black via-black/20 to-transparent z-10" />
                         <img 
                            src="https://images.unsplash.com/photo-1618843479313-40f8afb4b4d8?q=80&w=1000&auto=format&fit=crop"
                            className="w-full h-full object-cover filter brightness-75 group-hover:scale-110 transition-transform duration-1000"
                            alt="Premium Car"
                         />
                      </div>

                      {/* Info Overlay */}
                      <div className="relative z-20 h-full flex flex-col justify-between p-8">
                         <div className="self-end">
                            <span className="px-4 py-1.5 rounded-full bg-primary text-white text-[9px] font-black uppercase tracking-widest shadow-xl">
                              المختار لدينا
                            </span>
                         </div>

                         <div className="bg-black/70 backdrop-blur-xl rounded-2xl p-6 border border-white/10">
                            <h3 className="text-2xl font-black text-white mb-2 text-center">{featuredPackage.name}</h3>
                            <div className="flex items-center justify-center gap-1.5">
                               <span className="text-3xl font-black text-primary">{featuredPackage.price}</span>
                               <span className="text-xs font-black text-primary/70">د.ل</span>
                            </div>
                            <div className="mt-4 pt-4 border-t border-white/5 flex justify-center gap-2 items-center text-[8px] font-black uppercase tracking-tighter text-white/40">
                               <SparklesIcon size={14} className="text-primary/60" />
                               تجهيز احترافي شامل
                            </div>
                         </div>
                      </div>

                      {/* Glow Streak */}
                      <div className="absolute -inset-full top-0 h-full w-1/2 z-30 block transform -skew-x-12 bg-gradient-to-r from-transparent via-white/5 to-transparent opacity-0 group-hover:animate-shine pointer-events-none" />
                    </motion.div>
                    
                    {/* Atmospheric Bottom Glow */}
                    <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 w-2/3 h-10 bg-primary/30 blur-[40px] rounded-full pointer-events-none" />
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
